import React, { Fragment, FunctionComponent, useMemo } from 'react';
import { Typography, Tooltip, LockIcon, LockOpenIcon, useTheme, TTheme } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { AuthTypeValue } from '../utils/getAuthenticationTypes';

export interface AuthChipProps {
  authType: AuthTypeValue;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
}

interface AuthTypeConfig {
  labelKey: string;
  color: string;
  tooltipDescriptionKey: string;
  tooltipLinkHref?: string;
  IconComponent: typeof LockIcon | typeof LockOpenIcon;
}

export function getAuthTypeConfig(authType: AuthTypeValue, theme: TTheme): AuthTypeConfig | null {
  switch (authType) {
    case 'none':
      return {
        labelKey: 'Label.NoneAuth',
        color: theme.palette.actionV2.notice.fill,
        tooltipDescriptionKey: 'Description.NoneAuthWarning',
        IconComponent: LockOpenIcon,
      };
    case 'roblox-legacy-cookie':
      return {
        labelKey: 'Label.CookieAuth',
        color: theme.palette.actionV2.notice.fill,
        tooltipDescriptionKey: 'Description.LegacyAuthWarning',
        IconComponent: LockIcon,
      };
    case 'roblox-api-key':
      return {
        labelKey: 'Label.ApiKeyAuth',
        color: theme.palette.content.standard,
        tooltipDescriptionKey: 'Description.ApiKeyAuth',
        tooltipLinkHref: '/cloud/auth/api-keys',
        IconComponent: LockIcon,
      };
    case 'roblox-oauth2':
      return {
        labelKey: 'Label.OAuth2Auth',
        color: theme.palette.content.standard,
        tooltipDescriptionKey: 'Description.OAuth2Auth',
        tooltipLinkHref: '/cloud/auth/oauth2-overview',
        IconComponent: LockIcon,
      };
    default:
      return null;
  }
}

const AuthChip: FunctionComponent<AuthChipProps> = ({ authType, tooltipPlacement }) => {
  const { translate } = useTranslation();
  const theme = useTheme();
  const config = useMemo(() => getAuthTypeConfig(authType, theme), [authType, theme]);

  if (!config) return null;

  return (
    <Tooltip
      title={
        config.tooltipLinkHref ? (
          <Fragment>
            {translate(config.tooltipDescriptionKey)}{' '}
            <DynamicallyRouted.Link
              target='_blank'
              href={config.tooltipLinkHref}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
              style={{ textDecoration: 'underline', color: 'inherit' }}>
              {translate('Link.FindOutMore') || 'Find out more'}
            </DynamicallyRouted.Link>
          </Fragment>
        ) : (
          translate(config.tooltipDescriptionKey)
        )
      }
      {...(tooltipPlacement && { placement: tooltipPlacement })}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <config.IconComponent fontSize='small' style={{ color: config.color, width: '12px' }} />
        <Typography variant='body2' component='span' style={{ color: config.color }}>
          {translate(config.labelKey)}
        </Typography>
      </span>
    </Tooltip>
  );
};

export default AuthChip;
